import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Upload, Check, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
    { v: "food_drink", t: "Food & Drink" },
    { v: "beauty", t: "Beauty" },
    { v: "wellness", t: "Wellness" },
    { v: "fashion", t: "Fashion" },
    { v: "art", t: "Art" },
    { v: "service", t: "Service" },
    { v: "product", t: "Product" },
    { v: "other", t: "Other" },
];

const categoryLabel = (v) => CATEGORIES.find((c) => c.v === v)?.t || v;

export default function FeatureYourBusiness() {
    const [form, setForm] = useState({
        business_name: "", category: "food_drink", owner_name: "", email: "", phone: "", city: "",
        website: "", instagram: "", description: "", why_featured: "", photo_url: "", consent: true,
    });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);
    const [past, setPast] = useState([]);
    const [activeCat, setActiveCat] = useState("all");
    const [uploadingPhoto, setUploadingPhoto] = useState(false);

    const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value }));

    const uploadPhoto = async (file) => {
        if (!file) return;
        setUploadingPhoto(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post("/features/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const absolute = `${BACKEND_URL}${r.data.url}`;
            setForm((s) => ({ ...s, photo_url: absolute }));
            toast.success("Photo uploaded");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Photo upload failed");
        } finally {
            setUploadingPhoto(false);
        }
    };

    const removePhoto = () => setForm((s) => ({ ...s, photo_url: "" }));

    useEffect(() => {
        api.get("/features/past").then((r) => setPast(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }, []);

    const visibleCategories = useMemo(() => {
        const seen = new Set(past.map((p) => p.category));
        return [{ v: "all", t: "All" }, ...CATEGORIES.filter((c) => seen.has(c.v))];
    }, [past]);

    const filteredPast = useMemo(() => {
        if (activeCat === "all") return past;
        return past.filter((p) => p.category === activeCat);
    }, [past, activeCat]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try { await api.post("/features/apply", form); setDone(true); toast.success("Application received. We'll be in touch."); }
        catch (err) { toast.error(err?.response?.data?.detail?.[0]?.msg || "Something went wrong"); }
        finally { setBusy(false); }
    };

    if (done) return (
        <main className="pt-40 pb-40 min-h-screen" data-testid="feature-thankyou">
            <div className="mx-auto max-w-3xl px-6 text-center">
                <p className="eyebrow">Application received</p>
                <h1 className="font-display text-6xl md:text-8xl mt-6">Thank you.</h1>
                <p className="mt-8 text-[color:var(--cream-muted)]">We review applications every week and email selected businesses before we publish. There's no fee — ever.</p>
                <div className="mt-10"><Link to="/" className="btn-gold btn-gold-solid">Return home</Link></div>
            </div>
        </main>
    );

    return (
        <main data-testid="feature-page" className="pt-32 pb-32 min-h-screen">
            <section className="mx-auto max-w-[1600px] px-6 lg:px-12">
                <p className="eyebrow">Feature Your Business</p>
                <h1 className="font-display text-6xl md:text-8xl mt-4 max-w-4xl leading-[0.95]">Get on our feed. <em className="text-[color:var(--gold)]">For free</em>.</h1>
                <p className="mt-8 max-w-2xl text-[color:var(--cream-muted)] text-lg">
                    Every week we feature a local business, product, or service loved by our community — right here on the site. There is no fee to apply and no fee to be featured. If your work fits our audience, we'd love to know you.
                </p>
            </section>

            <section className="mx-auto max-w-[1600px] px-6 lg:px-12 mt-24 grid lg:grid-cols-12 gap-16">
                <div className="lg:col-span-5">
                    <p className="eyebrow">What we look for</p>
                    <ul className="mt-6 space-y-4 text-[color:var(--soft-cream)] font-serif text-xl">
                        {[
                            "Owned or led by women, especially from our community",
                            "Local, considered, and warm in feel",
                            "A story worth telling — not just a sales pitch",
                            "Willing to be tagged and re-shared across our channels",
                        ].map((x) => (<li key={x} className="border-b border-[color:var(--espresso)] pb-3">— {x}</li>))}
                    </ul>
                    <p className="mt-12 eyebrow">How it works</p>
                    <ol className="mt-6 space-y-3 text-sm text-[color:var(--cream-muted)] list-decimal list-inside">
                        <li>Submit the form. Takes about 4 minutes.</li>
                        <li>We review every week.</li>
                        <li>If selected, we email you and schedule your week.</li>
                        <li>You get a homepage feature, an "About the maker" write-up, and a social share to our audience.</li>
                    </ol>
                </div>

                <form onSubmit={submit} className="lg:col-span-7 border border-[color:var(--espresso)] p-10 space-y-6" data-testid="feature-form">
                    <div className="grid md:grid-cols-2 gap-6">
                        <input required className="input-line" placeholder="Business / brand name" value={form.business_name} onChange={set("business_name")} data-testid="feature-business-name" />
                        <div>
                            <label className="text-xs uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Category</label>
                            <select className="input-line" value={form.category} onChange={set("category")} data-testid="feature-category">
                                {CATEGORIES.map((c) => <option key={c.v} value={c.v} className="bg-[color:var(--warm-black)]">{c.t}</option>)}
                            </select>
                        </div>
                        <input required className="input-line" placeholder="Owner / contact name" value={form.owner_name} onChange={set("owner_name")} data-testid="feature-owner-name" />
                        <input required type="email" className="input-line" placeholder="Email" value={form.email} onChange={set("email")} data-testid="feature-email" />
                        <input className="input-line" placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} />
                        <input required className="input-line" placeholder="City" value={form.city} onChange={set("city")} data-testid="feature-city" />
                        <input className="input-line" placeholder="Website (optional)" value={form.website} onChange={set("website")} />
                        <input className="input-line" placeholder="Instagram handle (optional)" value={form.instagram} onChange={set("instagram")} />
                    </div>
                    <textarea required className="input-line" placeholder="Tell us about your business in a few sentences." value={form.description} onChange={set("description")} data-testid="feature-description" />
                    <textarea required className="input-line" placeholder="Why is this the right time to feature you?" value={form.why_featured} onChange={set("why_featured")} data-testid="feature-why" />

                    <div data-testid="feature-photo-uploader">
                        <p className="text-xs uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">Your best photo (optional but encouraged)</p>
                        <p className="text-[0.7rem] text-[color:var(--cream-muted)] mt-1 italic">Horizontal photos work best. JPG, PNG, WebP or HEIC · up to 25 MB.</p>
                        <div className="mt-3 flex flex-wrap items-center gap-4">
                            <label className={"inline-flex items-center gap-2 btn-gold cursor-pointer " + (uploadingPhoto ? "opacity-40 pointer-events-none" : "")}>
                                <Upload size={14} />
                                {uploadingPhoto ? "Uploading…" : (form.photo_url ? "Replace photo" : "Upload photo")}
                                <input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])} data-testid="feature-photo-input" />
                            </label>
                            {form.photo_url && (
                                <div className="flex items-center gap-3">
                                    <div className="relative border border-[color:var(--espresso)]">
                                        <img src={form.photo_url} alt="submission preview" className="w-24 h-24 object-cover" data-testid="feature-photo-preview" />
                                        <button type="button" onClick={removePhoto} className="absolute -top-2 -right-2 bg-[color:var(--warm-black)] border border-[color:var(--espresso)] text-[color:var(--cream-muted)] hover:text-[color:var(--wine)] p-1" aria-label="Remove photo" data-testid="feature-photo-remove"><X size={12} /></button>
                                    </div>
                                    <span className="text-xs text-[color:var(--gold)] flex items-center gap-1"><Check size={14} /> Uploaded</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <label className="flex items-start gap-3 text-xs text-[color:var(--cream-muted)]">
                        <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-1 accent-[color:var(--gold)]" data-testid="feature-consent" />
                        <span>I agree to Mrs.Black(In)Love using my submitted materials to feature my business on the website and social channels. There is no fee for this feature.</span>
                    </label>
                    <button type="submit" disabled={busy || !form.consent} className="btn-gold btn-gold-solid w-full justify-center disabled:opacity-40" data-testid="feature-submit">
                        {busy ? "Sending…" : "Submit application"}
                    </button>
                    <p className="text-[0.65rem] text-[color:var(--cream-muted)]">Free to apply. Free to be featured. We never charge for editorial.</p>
                </form>
            </section>

            {/* PAST FEATURES ARCHIVE */}
            {past.length > 0 && (
                <section className="mx-auto max-w-[1600px] px-6 lg:px-12 mt-32 border-t border-[color:var(--espresso)] pt-24" data-testid="past-features">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
                        <div>
                            <p className="eyebrow">Previously featured</p>
                            <h2 className="font-display text-5xl md:text-7xl mt-4 max-w-3xl leading-[0.95]">
                                Businesses we've <em className="text-[color:var(--gold)]">loved</em>.
                            </h2>
                        </div>
                        <p className="text-xs uppercase tracking-[0.24em] text-[color:var(--cream-muted)]">
                            {filteredPast.length} feature{filteredPast.length === 1 ? "" : "s"}
                        </p>
                    </div>

                    {/* Category filter chips */}
                    <div className="mt-12 flex flex-wrap gap-3" data-testid="past-features-filter">
                        {visibleCategories.map((c) => {
                            const active = activeCat === c.v;
                            return (
                                <button
                                    key={c.v}
                                    onClick={() => setActiveCat(c.v)}
                                    data-testid={`filter-chip-${c.v}`}
                                    className={
                                        "text-xs uppercase tracking-[0.24em] px-5 py-2 border transition-colors duration-300 " +
                                        (active
                                            ? "bg-[color:var(--gold)] border-[color:var(--gold)] text-[color:var(--warm-black)]"
                                            : "border-[color:var(--espresso)] text-[color:var(--cream-muted)] hover:text-[color:var(--gold)] hover:border-[color:var(--gold)]")
                                    }
                                >
                                    {c.t}
                                </button>
                            );
                        })}
                    </div>

                    <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                        {filteredPast.map((p) => (
                            <div key={p.id} className="group" data-testid={`past-feature-${p.id}`}>
                                {p.photo_url && (
                                    <div className="zoom-parent overflow-hidden">
                                        <img src={p.photo_url} alt={p.business_name} className="h-[380px] w-full object-cover" />
                                    </div>
                                )}
                                <div className="mt-5">
                                    <p className="text-xs tracking-[0.24em] uppercase text-[color:var(--gold)]">
                                        {categoryLabel(p.category)}{p.city ? ` · ${p.city}` : ""}
                                    </p>
                                    <h3 className="font-serif text-2xl mt-2 text-[color:var(--soft-cream)]">{p.business_name}</h3>
                                    {p.description && (
                                        <p className="mt-3 text-sm text-[color:var(--cream-muted)] line-clamp-3">{p.description}</p>
                                    )}
                                    <div className="mt-4 flex flex-wrap gap-4 text-xs">
                                        {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="link-underline uppercase tracking-[0.24em] text-[color:var(--gold)]">Website</a>}
                                        {p.instagram && <a href={`https://instagram.com/${p.instagram.replace('@','')}`} target="_blank" rel="noreferrer" className="link-underline uppercase tracking-[0.24em] text-[color:var(--gold)]">@{p.instagram.replace('@','')}</a>}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {filteredPast.length === 0 && (
                        <p className="mt-14 text-[color:var(--cream-muted)] text-sm">No past features in this category yet.</p>
                    )}
                </section>
            )}
        </main>
    );
}
